// MultiGradeView.js
// -----------------------------------------------------------------------------
// Forked from NewGrading/NewGrading.js.
//
// Why a fork and not a reuse: NewGrading fetches its own grade on mount, by
// caseId, via /viewcasegrade.webapi -> newest CustomerGradeId. Inside a block we
// need THIS attempt's grade, not the newest one — a case redone outside the
// block would otherwise show the wrong grade. No prop can redirect that, so the
// fetch has to go, and once it goes most of the component's plumbing goes too.
//
// What's kept: score cards, the six graded sections, the case summary, the
// average-orders distribution chart, the action log, the highlighter, reading
// options, high yield, and review later.
//
// What's dropped: "Try Case Again" and "View Previous Completions" (both fight
// the idea of a finished block), the navbar (the review sidebar navigates), and
// the self-fetch (MultiReview passes the grade in).
//
// It also does NOT touch document-level listeners or <meta name=theme-color>
// the way NewGrading does: this renders inside the MultiCCS overlay, and an
// unmount here must leave the host app exactly as it found it. Copy protection
// is scoped to this subtree instead.
//
// Reuses NewGrading.css — same classes, same look as the main grading page.
//
// Props:
//   grade            grade envelope from /retreivemulticasegrade.webapi (required)
//   caseRow          merged case row for the title block               (required)
//   api              MultiCCS api layer                                (required)
//   userData         login payload (TimeZone for date formatting)      (required)
//   readingOptions   saved reading options, or null                    (optional)
//   onReadingOptions (next) => void — persist                          (optional)
//   reviewLater      bool                                              (required)
//   onReviewLater    (caseId, marked) => void                          (required)
//   primaryColor     string                                            (optional)
//   selectedTheme    theme object                                      (optional)
//   animationsDisabled bool                                            (optional)
//   disableFeedback  bool — user turned feedback colors off            (optional)
//   hideFeedbackInGrading bool                                         (optional)
// -----------------------------------------------------------------------------

import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faQuestionCircle,
  faHighlighter,
  faEraser,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { faUserDoctorMessage } from "@fortawesome/pro-solid-svg-icons";
import { Line } from "react-chartjs-2";
import MDSpinner from "react-md-spinner";
import ActionLog from "../NewGrading/Components/ActionLog/ActionLog";
import formatDateString from "../Functions/FormatDateString";
import formatTimeString from "../Functions/FormatTimeString";
import "../NewGrading/NewGrading.css";

const GRADING_SECTIONS = [
  {
    title: "Diagnosis Orders",
    gradeValue: "FinalDiagnoisGrade",
    weightValue: "WEIGHTDIAGNOSIS",
  },
  {
    title: "Treatment Orders",
    gradeValue: "FinalTreatmentGrade",
    weightValue: "WEIGHTTREATMENT",
  },
  {
    title: "Preventive care",
    gradeValue: "FinalPreventiveCareGrade",
    weightValue: "WEIGHTPREVENTATIVE",
  },
  {
    title: "Timing",
    gradeValue: "FinalTimingGrade",
    weightValue: "WEIGHTTIMING",
  },
  {
    title: "Appropriate Orders",
    gradeValue: "FinalAppropriateGrade",
    weightValue: "WEIGHTAPPROPRIATE",
  },
  {
    title: "Appropriate Location / Appropriate Sequence",
    gradeValue: "FinalSequenceGrade",
    weightValue: "WEIGHTSEQUENCING",
  },
];

const DEFAULT_READING_OPTIONS = {
  readingFontSize: "medium",
  readingFontFamily: "sans",
  readingLineHeight: "normal",
  readingLetterSpacing: "normal",
  readingColorScheme: "default",
};

class MultiGradeView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedActionLogItem: 0,
      highYield: !!(props.grade && props.grade.HighYieldFlag),
      highYieldLoading: false,
      reviewLaterLoading: false,
      showReadingOptions: false,
      readingOptions: Object.assign(
        {},
        DEFAULT_READING_OPTIONS,
        props.readingOptions || {},
      ),
      highlighterActive: false,
      highlights: [],
    };

    this.pageRef = React.createRef();
    // Registry key is per-case so switching cases in the sidebar can't leave a
    // previous case's ranges registered.
    this.HIGHLIGHT_REGISTRY_KEY =
      "multiGradingHighlights-" + (props.caseRow ? props.caseRow.ID : "x");
    this.highlightApiSupported =
      typeof window !== "undefined" &&
      typeof window.Highlight === "function" &&
      typeof CSS !== "undefined" &&
      !!CSS.highlights;
  }

  componentDidMount() {
    document.addEventListener("selectionchange", this.handleSelectionChange);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.readingOptions !== this.props.readingOptions) {
      this.setState({
        readingOptions: Object.assign(
          {},
          DEFAULT_READING_OPTIONS,
          this.props.readingOptions || {},
        ),
      });
    }

    if (prevProps.grade !== this.props.grade) {
      // Case switched — any pending debounced selection from the previous
      // case is now stale (it would resolve against the wrong container
      // set / registry key), so drop it.
      if (this.selectionSettleTimer) {
        clearTimeout(this.selectionSettleTimer);
        this.selectionSettleTimer = null;
      }
      this.setState({
        highYield: !!(this.props.grade && this.props.grade.HighYieldFlag),
        highlights: [],
        selectedActionLogItem: 0,
      });
    }

    if (
      prevState.highlights !== this.state.highlights ||
      prevProps.grade !== this.props.grade ||
      prevState.readingOptions !== this.state.readingOptions
    ) {
      this.applyHighlightsToDom();
    }
  }

  componentWillUnmount() {
    if (this.highlightApiSupported) {
      CSS.highlights.delete(this.HIGHLIGHT_REGISTRY_KEY);
    }

    // --- Highlighter (mobile) ---
    document.removeEventListener("selectionchange", this.handleSelectionChange);
    if (this.selectionSettleTimer) {
      clearTimeout(this.selectionSettleTimer);
    }
  }

  // --- copy protection, scoped to this subtree -------------------------------
  // NewGrading binds these on `document`; here they're React handlers on the
  // wrapper so nothing leaks out of the overlay.

  blockClipboard = (e) => {
    e.preventDefault();
  };

  blockContextMenu = (e) => {
    e.preventDefault();
  };

  blockKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "x")) {
      e.preventDefault();
    }
  };

  // --- review later / high yield --------------------------------------------

  handleReviewLater = () => {
    const caseId = this.props.caseRow.ID;
    const next = !this.props.reviewLater;
    this.setState({ reviewLaterLoading: true });
    this.props.api
      .saveReviewLater(caseId, next)
      .then(() => {
        this.setState({ reviewLaterLoading: false });
        this.props.onReviewLater(caseId, next);
      })
      .catch(() => {
        this.setState({ reviewLaterLoading: false });
      });
  };

  handleHighYield = () => {
    const caseId = this.props.caseRow.ID;
    const next = !this.state.highYield;
    // Optimistic, reverted on failure — same as NewGrading.
    this.setState({ highYield: next, highYieldLoading: true });
    this.props.api
      .saveHighYield(caseId, next)
      .then(() => {
        this.setState({ highYieldLoading: false });
      })
      .catch(() => {
        this.setState({ highYield: !next, highYieldLoading: false });
      });
  };

  // --- reading options -------------------------------------------------------

  patchReadingOptions = (changes) => {
    const next = Object.assign({}, this.state.readingOptions, changes);
    this.setState({ readingOptions: next });
    if (this.props.onReadingOptions) this.props.onReadingOptions(next);
  };

  resetReadingOptions = () => {
    const next = Object.assign({}, DEFAULT_READING_OPTIONS);
    this.setState({ readingOptions: next });
    if (this.props.onReadingOptions) this.props.onReadingOptions(next);
  };

  getReadingStyle = (verdict) => {
    const options = this.state.readingOptions;
    const fontSizeMap = {
      small: "13px",
      medium: "15px",
      large: "17px",
      xlarge: "20px",
    };
    const fontFamilyMap = {
      sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      serif: "Georgia, 'Times New Roman', serif",
      mono: "'Courier New', Courier, monospace",
    };
    const lineHeightMap = { compact: "1.4", normal: "1.7", relaxed: "2.1" };
    const letterSpacingMap = { normal: "0px", wide: "0.04em" };

    const colorSchemes = {
      default: {
        neutral: { color: "var(--new-text-color)" },
      },
      mono: {
        correct: { backgroundColor: "#f0f0f0", color: "#1a1a1a" },
        incorrect: { backgroundColor: "#8a8a8a", color: "#f5f5f5" },
        optional: { backgroundColor: "#cbcbcb", color: "#1a1a1a" },
        neutral: { backgroundColor: "#e0e0e0", color: "#1a1a1a" },
      },
      sepia: {
        correct: { backgroundColor: "#f0e8cf", color: "#3b2f14" },
        incorrect: { backgroundColor: "#b89a70", color: "#2a1f0a" },
        optional: { backgroundColor: "#ddd0b0", color: "#3b2f14" },
        neutral: { backgroundColor: "#ede5cc", color: "#3b2f14" },
      },
      cool: {
        correct: { backgroundColor: "#e8f4fc", color: "#0d2a3a" },
        incorrect: { backgroundColor: "#7aafd4", color: "#0d1e2e" },
        optional: { backgroundColor: "#c0daf0", color: "#0d2a3a" },
        neutral: { backgroundColor: "#daeaf7", color: "#0d2a3a" },
      },
      highcontrast: {
        correct: { backgroundColor: "#003300", color: "#ccffcc" },
        incorrect: { backgroundColor: "#330000", color: "#ffcccc" },
        optional: { backgroundColor: "#1a1a00", color: "#ffffcc" },
        neutral: { backgroundColor: "#00134d", color: "#cce0ff" },
      },
    };

    const scheme = colorSchemes[options.readingColorScheme];
    const colorOverride = (scheme && scheme[verdict || "neutral"]) || {};

    return Object.assign(
      {
        fontSize: fontSizeMap[options.readingFontSize],
        fontFamily: fontFamilyMap[options.readingFontFamily],
        lineHeight: lineHeightMap[options.readingLineHeight],
        letterSpacing: letterSpacingMap[options.readingLetterSpacing],
      },
      colorOverride,
    );
  };

  // --- highlighter -----------------------------------------------------------
  // Same approach as NewGrading: store character offsets within a labelled
  // container, re-anchor by text if the offsets drift, and paint with the CSS
  // Custom Highlight API where available, <mark> wrapping where not.

  toggleHighlighter = () => {
    this.setState(function (prevState) {
      return { highlighterActive: !prevState.highlighterActive };
    });
  };

  clearAllHighlights = () => {
    this.setState({ highlights: [] });
  };

  findHighlightContainer = (containerKey) => {
    const root = this.pageRef.current || document;
    const containers = root.querySelectorAll(
      "[data-highlight-container='true']",
    );
    for (let i = 0; i < containers.length; i++) {
      if (containers[i].dataset.highlightKey === containerKey) {
        return containers[i];
      }
    }
    return null;
  };

  getTextNodesIn = (container) => {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) {
      nodes.push(node);
    }
    return nodes;
  };

  getOffsetWithin = (container, node, offset) => {
    try {
      const range = document.createRange();
      range.selectNodeContents(container);
      range.setEnd(node, offset);
      return range.toString().length;
    } catch (error) {
      return null;
    }
  };

  createRangeFromOffsets = (container, start, end) => {
    const textNodes = this.getTextNodesIn(container);
    let charCount = 0;
    let startNode = null;
    let startOffset = 0;
    let endNode = null;
    let endOffset = 0;

    for (let i = 0; i < textNodes.length; i++) {
      const node = textNodes[i];
      const length = node.textContent.length;
      if (startNode === null && start <= charCount + length) {
        startNode = node;
        startOffset = start - charCount;
      }
      if (startNode !== null && end <= charCount + length) {
        endNode = node;
        endOffset = end - charCount;
        break;
      }
      charCount += length;
    }

    if (!startNode || !endNode) return null;

    try {
      const range = document.createRange();
      range.setStart(startNode, startOffset);
      range.setEnd(endNode, endOffset);
      return range;
    } catch (error) {
      return null;
    }
  };

  applyHighlightsToDom = () => {
    if (this.highlightApiSupported) {
      CSS.highlights.delete(this.HIGHLIGHT_REGISTRY_KEY);
    } else {
      this.clearHighlightMarks();
    }

    if (this.state.highlights.length === 0) return;

    const resolvedRanges = [];
    this.state.highlights.forEach((highlight) => {
      const container = this.findHighlightContainer(highlight.containerKey);
      if (!container) return;

      let range = this.createRangeFromOffsets(
        container,
        highlight.startOffset,
        highlight.endOffset,
      );

      // Offsets can drift if the text re-renders; fall back to finding the
      // stored text.
      if (!range || range.toString() !== highlight.text) {
        const fullText = container.textContent;
        const index = fullText.indexOf(highlight.text);
        if (index === -1) return;
        range = this.createRangeFromOffsets(
          container,
          index,
          index + highlight.text.length,
        );
        if (!range) return;
      }

      resolvedRanges.push({ range: range, id: highlight.id });
    });

    if (resolvedRanges.length === 0) return;

    if (this.highlightApiSupported) {
      CSS.highlights.set(
        this.HIGHLIGHT_REGISTRY_KEY,
        new window.Highlight(
          ...resolvedRanges.map(function (entry) {
            return entry.range;
          }),
        ),
      );
    } else {
      resolvedRanges
        .slice()
        .reverse()
        .forEach((entry) => this.wrapRangeInMarks(entry.range, entry.id));
    }
  };

  wrapRangeInMarks = (range, highlightId) => {
    const root =
      range.commonAncestorContainer.nodeType === Node.TEXT_NODE
        ? range.commonAncestorContainer.parentNode
        : range.commonAncestorContainer;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const intersecting = [];
    let node;
    while ((node = walker.nextNode())) {
      if (range.intersectsNode(node)) intersecting.push(node);
    }

    intersecting.reverse().forEach((textNode) => {
      const start = textNode === range.startContainer ? range.startOffset : 0;
      const end =
        textNode === range.endContainer
          ? range.endOffset
          : textNode.textContent.length;
      if (end <= start) return;

      let nodeToWrap = textNode;
      if (start > 0) nodeToWrap = textNode.splitText(start);
      if (end - start < nodeToWrap.textContent.length) {
        nodeToWrap.splitText(end - start);
      }

      const mark = document.createElement("mark");
      mark.className = "newGradingTextHighlight";
      mark.dataset.highlightId = highlightId;
      nodeToWrap.parentNode.insertBefore(mark, nodeToWrap);
      mark.appendChild(nodeToWrap);
    });
  };

  clearHighlightMarks = () => {
    const root = this.pageRef.current || document;
    root.querySelectorAll("mark.newGradingTextHighlight").forEach((mark) => {
      const parent = mark.parentNode;
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
      parent.removeChild(mark);
      parent.normalize();
    });
  };

  // Extracts { clientX, clientY } from either a MouseEvent or a TouchEvent
  // (touchend events carry their coordinates on changedTouches, not on the
  // event itself).
  getEventPoint = (event) => {
    if (event.changedTouches && event.changedTouches.length > 0) {
      return {
        clientX: event.changedTouches[0].clientX,
        clientY: event.changedTouches[0].clientY,
      };
    }
    return { clientX: event.clientX, clientY: event.clientY };
  };

  // Turns the current, non-collapsed window selection into a stored
  // highlight (if it falls inside a highlight-able container). Shared by the
  // desktop mouseup path and the mobile selectionchange path.
  commitSelectionAsHighlight = (selection) => {
    const rawRange = selection.getRangeAt(0);
    const startElement =
      rawRange.startContainer.nodeType === Node.TEXT_NODE
        ? rawRange.startContainer.parentElement
        : rawRange.startContainer;
    const container =
      startElement &&
      startElement.closest &&
      startElement.closest("[data-highlight-container='true']");

    if (!container) {
      selection.removeAllRanges();
      return;
    }

    const range = rawRange.cloneRange();
    if (!container.contains(range.endContainer)) {
      range.setEnd(container, container.childNodes.length);
    }

    const startOffset = this.getOffsetWithin(
      container,
      range.startContainer,
      range.startOffset,
    );
    const endOffset = this.getOffsetWithin(
      container,
      range.endContainer,
      range.endOffset,
    );

    if (
      startOffset === null ||
      endOffset === null ||
      endOffset <= startOffset
    ) {
      selection.removeAllRanges();
      return;
    }

    const text = range.toString();
    if (!text.trim()) {
      selection.removeAllRanges();
      return;
    }

    const highlight = {
      id: "hl-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
      containerType: container.dataset.highlightType,
      containerKey: container.dataset.highlightKey,
      startOffset: startOffset,
      endOffset: endOffset,
      text: text,
      createdAt: new Date().toISOString(),
    };

    selection.removeAllRanges();
    this.setState(function (prevState) {
      return { highlights: prevState.highlights.concat([highlight]) };
    });
  };

  handleHighlightMouseUp = (event) => {
    if (!this.state.highlighterActive) return;

    const selection = window.getSelection();
    if (!selection) return;

    if (selection.isCollapsed || selection.rangeCount === 0) {
      this.removeHighlightAtPoint(event);
      return;
    }

    this.commitSelectionAsHighlight(selection);
  };

  handleSelectionChange = () => {
    if (!this.state.highlighterActive) return;

    if (this.selectionSettleTimer) {
      clearTimeout(this.selectionSettleTimer);
    }

    this.selectionSettleTimer = setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return;
      }
      this.commitSelectionAsHighlight(selection);
    }, 300);
  };

  handleHighlightTouchEnd = (event) => {
    if (!this.state.highlighterActive) return;

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
      return;
    }

    this.removeHighlightAtPoint(event);
  };

  removeHighlightAtPoint = (event) => {
    const markElement =
      event.target &&
      event.target.closest &&
      event.target.closest("mark.newGradingTextHighlight");
    if (markElement) {
      const id = markElement.dataset.highlightId;
      this.setState(function (prevState) {
        return {
          highlights: prevState.highlights.filter(function (h) {
            return h.id !== id;
          }),
        };
      });
      return;
    }

    const container =
      event.target &&
      event.target.closest &&
      event.target.closest("[data-highlight-container='true']");
    if (!container) return;

    const point = this.getEventPoint(event);
    const clientX = point.clientX;
    const clientY = point.clientY;

    let node = null;
    let offset = 0;
    if (document.caretPositionFromPoint) {
      const position = document.caretPositionFromPoint(clientX, clientY);
      if (position) {
        node = position.offsetNode;
        offset = position.offset;
      }
    } else if (document.caretRangeFromPoint) {
      const caretRange = document.caretRangeFromPoint(clientX, clientY);
      if (caretRange) {
        node = caretRange.startContainer;
        offset = caretRange.startOffset;
      }
    }

    if (!node || !container.contains(node)) return;

    const globalOffset = this.getOffsetWithin(container, node, offset);
    if (globalOffset === null) return;

    const containerKey = container.dataset.highlightKey;
    const match = this.state.highlights.find(function (h) {
      return (
        h.containerKey === containerKey &&
        globalOffset >= h.startOffset &&
        globalOffset <= h.endOffset
      );
    });
    if (match) {
      this.setState(function (prevState) {
        return {
          highlights: prevState.highlights.filter(function (h) {
            return h.id !== match.id;
          }),
        };
      });
    }
  };

  // --- average orders chart --------------------------------------------------

  getAverageOrders() {
    const grade = this.props.grade;
    if (!grade || !grade.GradeFeedback) return null;
    const wrapper = grade.GradeFeedback.AverageOrders;
    if (!wrapper || !wrapper.AverageOrders) return null;
    if (Object.keys(wrapper.AverageOrders).length === 0) return null;
    return wrapper;
  }

  getAverageOrdersChartData = () => {
    const wrapper = this.getAverageOrders();
    const averageOrders = wrapper.AverageOrders;
    const userOrders = wrapper.NumberOrders ? wrapper.NumberOrders : 0;
    const average85Above = averageOrders.average_num_orders_85_above;
    const stdDev = averageOrders.std_dev_orders_85_above;

    const dataPoints = [];
    const labels = [];

    const minX = Math.max(0, average85Above - 3 * stdDev);
    const maxX = average85Above + 3 * stdDev;
    const step = (maxX - minX) / 50;

    for (let x = minX; x <= maxX; x += step) {
      labels.push(Math.round(x));
      const exponent = -0.5 * Math.pow((x - average85Above) / stdDev, 2);
      dataPoints.push(
        (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent),
      );
    }

    const userPosition = Math.max(
      0,
      Math.min(labels.length - 1, Math.round((userOrders - minX) / step)),
    );

    return {
      labels: labels,
      datasets: [
        {
          label: "Distribution of Orders (>85% scores)",
          data: dataPoints,
          borderColor: "#4F46E5",
          backgroundColor: "rgba(79, 70, 229, 0.1)",
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
        {
          label: "Your Performance",
          data: labels.map(function (_, index) {
            return index === userPosition ? dataPoints[userPosition] : null;
          }),
          borderColor: wrapper.Color === "Green" ? "#10B981" : "#EF4444",
          backgroundColor: wrapper.Color === "Green" ? "#10B981" : "#EF4444",
          pointRadius: 8,
          pointHoverRadius: 10,
          showLine: false,
        },
      ],
    };
  };

  getAverageOrdersChartOptions = () => {
    const wrapper = this.getAverageOrders();
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: "Order Count Distribution (First Attempts >85%)",
          color: "#374151",
          font: { size: 14, weight: "bold" },
        },
        legend: {
          display: true,
          position: "top",
          labels: { color: "#374151", usePointStyle: true },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            title: function (context) {
              return "Orders: " + context[0].label;
            },
            label: function (context) {
              if (context.datasetIndex === 0) {
                return "Probability Density: " + context.parsed.y.toFixed(4);
              }
              return (
                "Your Position (Z-score: " + wrapper.Zscore.toFixed(2) + ")"
              );
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: "Number of Orders",
            color: "#374151",
            font: { weight: "bold" },
          },
          grid: { color: "rgba(0, 0, 0, 0.1)" },
          ticks: {
            color: "#6B7280",
            callback: function (value, index) {
              if (index % 5 === 0) return this.getLabelForValue(value);
              return "";
            },
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: "Probability Density",
            color: "#374151",
            font: { weight: "bold" },
          },
          grid: { color: "rgba(0, 0, 0, 0.1)" },
          ticks: {
            color: "#6B7280",
            callback: function (value) {
              return value.toFixed(3);
            },
          },
        },
      },
      interaction: { mode: "nearest", axis: "x", intersect: false },
    };
  };

  // --- importance indicator --------------------------------------------------

  getImportanceLabel(importance) {
    const labels = {
      5: "Critical",
      4: "High",
      3: "Moderate",
      2: "Low",
      1: "Minor",
    };
    return labels[importance] || "";
  }

  renderImportanceIndicator(importance, variant) {
    if (
      importance === undefined ||
      importance === null ||
      isNaN(importance) ||
      importance < 1 ||
      importance > 5 ||
      variant === "neutral"
    ) {
      return null;
    }

    const shadesByVariant = {
      incorrect: "#A32D2D",
      correct: "#27500A",
      neutral: "#1B4B52",
    };
    const emptyColorByVariant = {
      incorrect: "#F7C1C1",
      correct: "#C0DD97",
      neutral: "#C7DEE2",
    };

    const shade = shadesByVariant[variant] || shadesByVariant.neutral;
    const emptyColor =
      emptyColorByVariant[variant] || emptyColorByVariant.neutral;

    return (
      <div className="newGradingImportanceWrap">
        <div className="newGradingImportanceBar">
          {[1, 2, 3, 4, 5].map(function (level) {
            return (
              <div
                key={level}
                className="newGradingImportanceSeg"
                style={{
                  backgroundColor: level <= importance ? shade : emptyColor,
                }}
              />
            );
          })}
        </div>
        <span className="newGradingImportanceCaption">
          {this.getImportanceLabel(importance)}
        </span>
      </div>
    );
  }

  // --- render ---------------------------------------------------------------

  renderReadingOptions() {
    const options = this.state.readingOptions;

    return (
      <div className="readingOptionsWrapper">
        <button
          type="button"
          className="newGradingViewPreviousButton tertiaryButton readingOptionsToggle"
          onClick={() => {
            this.setState(function (prevState) {
              return { showReadingOptions: !prevState.showReadingOptions };
            });
          }}
        >
          <span className="readingOptionsIcon">Aa</span>
          <span className="readingOptionsText">Reading Options</span>
        </button>
        {this.state.showReadingOptions && (
          <>
            <div
              className="readingOptionsBackdrop"
              onClick={() => {
                this.setState({ showReadingOptions: false });
              }}
            />
            <div
              className="readingOptionsPanel"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Font Size</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "small", label: "S" },
                    { key: "medium", label: "M" },
                    { key: "large", label: "L" },
                    { key: "xlarge", label: "XL" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingFontSize === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({ readingFontSize: key });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Typeface</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "sans", label: "Sans" },
                    { key: "serif", label: "Serif" },
                    { key: "mono", label: "Mono" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingFontFamily === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({ readingFontFamily: key });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Line Spacing</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "compact", label: "Compact" },
                    { key: "normal", label: "Normal" },
                    { key: "relaxed", label: "Relaxed" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingLineHeight === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({ readingLineHeight: key });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Letter Spacing</span>
                <div className="readingOptionsRow">
                  {[
                    { key: "normal", label: "Normal" },
                    { key: "wide", label: "Wide" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingOptionChip" +
                        (options.readingLetterSpacing === key
                          ? " readingOptionChipActive"
                          : "")
                      }
                      onClick={() => {
                        this.patchReadingOptions({
                          readingLetterSpacing: key,
                        });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="readingOptionsSection">
                <span className="readingOptionsLabel">Color Scheme</span>
                <div className="readingOptionsColorRow">
                  {[
                    {
                      key: "default",
                      label: "Default",
                      swatches: ["#e2ffdd", "#ffdddd", "#d1ecf1"],
                    },
                    {
                      key: "mono",
                      label: "Mono",
                      swatches: ["#f0f0f0", "#8a8a8a", "#cbcbcb"],
                    },
                    {
                      key: "sepia",
                      label: "Sepia",
                      swatches: ["#f0e8cf", "#b89a70", "#ddd0b0"],
                    },
                    {
                      key: "cool",
                      label: "Cool",
                      swatches: ["#e8f4fc", "#7aafd4", "#c0daf0"],
                    },
                    {
                      key: "highcontrast",
                      label: "High Contrast",
                      swatches: ["#003300", "#330000", "#00134d"],
                    },
                  ].map(({ key, label, swatches }) => (
                    <button
                      key={key}
                      type="button"
                      className={
                        "readingColorChip" +
                        (options.readingColorScheme === key
                          ? " readingColorChipActive"
                          : "")
                      }
                      title={label}
                      onClick={() => {
                        this.patchReadingOptions({ readingColorScheme: key });
                      }}
                    >
                      <span className="readingColorSwatches">
                        {swatches.map(function (bg, i) {
                          return (
                            <span
                              key={i}
                              className="readingColorSwatch"
                              style={{ backgroundColor: bg }}
                            />
                          );
                        })}
                      </span>
                      <span className="readingColorLabel">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="readingOptionsReset"
                onClick={this.resetReadingOptions}
              >
                Reset to defaults
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  render() {
    const { grade, caseRow, userData, primaryColor, reviewLater } = this.props;

    if (!grade || !grade.GradeFeedback) {
      return (
        <div className="newGradingPageWrapper">
          <div className="newGradingLoaderContainer">
            <MDSpinner size={60} singleColor={primaryColor} />
            <p className="newGradingLoaderText">Loading grades</p>
          </div>
        </div>
      );
    }

    const feedback = grade.GradeFeedback;
    // The user's global "disable feedback" preference greys out the right/wrong
    // colouring, same as the main grading page.
    const showVerdicts = !this.props.disableFeedback;

    return (
      <div
        className={
          "newGradingPageWrapper" +
          (this.state.highlighterActive ? " newGradingHighlighterActive" : "")
        }
        ref={this.pageRef}
        onMouseUp={this.handleHighlightMouseUp}
        onTouchEnd={this.handleHighlightTouchEnd}
        onCopy={this.blockClipboard}
        onCut={this.blockClipboard}
        onContextMenu={this.blockContextMenu}
        onKeyDown={this.blockKeyDown}
      >
        <style>{`
          ::highlight(${this.HIGHLIGHT_REGISTRY_KEY}) {
            background-color: rgba(255, 214, 10, 0.55);
          }
          mark.newGradingTextHighlight {
            background-color: rgba(255, 214, 10, 0.55);
            color: inherit;
            padding: 0;
          }
          .newGradingHighlighterActive [data-highlight-container='true'] {
            cursor: text;
          }
          .newGradingHighlighterActive [data-highlight-container='true'] ::selection {
            background-color: rgba(255, 214, 10, 0.45);
          }
        `}</style>

        {/* Title block */}
        <div className="newGradingCaseTitleContainer">
          <div className="newGradingCaseNumberContainer">
            <h1 className="newGradingCaseNumber">{caseRow.ID}</h1>
          </div>
          <div className="newGradingCaseTitleWrapper">
            <h2 className="newGradingCaseTitle">
              {caseRow.TITLE} - {caseRow.CASECATEGORYDESC}
            </h2>
            <h3 className="newGradingCaseCompletionDate">
              Completed:{" "}
              {formatDateString(grade.CaseCompletionDate, userData.TimeZone)},{" "}
              {formatTimeString(grade.CaseCompletionDate, userData.TimeZone)}
            </h3>
          </div>
          <div className="grading-button-block">
            <div className="highlightingContainer">
              <button
                type="button"
                className="tertiaryButton newGradingBackButton"
                onClick={this.toggleHighlighter}
                title={
                  this.state.highlighterActive
                    ? "Highlighter on \u2014 select text to highlight it, click a highlight to remove it"
                    : "Turn on the highlighter"
                }
                style={
                  this.state.highlighterActive
                    ? { boxShadow: "0 0 0 2px rgba(255, 214, 10, 0.9) inset" }
                    : {}
                }
              >
                <FontAwesomeIcon
                  icon={faHighlighter}
                  className="newGradingBackIcon"
                />
                {this.state.highlighterActive
                  ? "Highlighter On"
                  : "Highlighter"}
                {this.state.highlights.length > 0 &&
                  " (" + this.state.highlights.length + ")"}
              </button>
              {this.state.highlights.length > 0 && (
                <button
                  type="button"
                  className="tertiaryButton newGradingBackButton"
                  onClick={this.clearAllHighlights}
                  title="Remove all highlights"
                >
                  <FontAwesomeIcon
                    icon={faEraser}
                    className="newGradingBackIcon"
                  />
                  Clear Highlights
                </button>
              )}
            </div>

            <div
              className="newGradingCheckboxContainer"
              onClick={this.handleHighYield}
              style={{
                gridTemplateColumns: "30px auto auto",
                paddingRight: "10px",
              }}
            >
              <div className="newGradingCheckbox">
                {this.state.highYield && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="newGradingCheckboxIcon"
                  />
                )}
              </div>
              <p className="newGradingCheckboxText">High Yield</p>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="highYieldMoreInfoButton"
                title="Mark this case if you think it has a good chance of showing up on the exam."
              />
              {this.state.highYieldLoading && (
                <div className="highYieldLoadingContainer">
                  <MDSpinner size={35} singleColor={"#fff"} />
                </div>
              )}
            </div>

            <div
              className="newGradingCheckboxContainer"
              onClick={this.handleReviewLater}
              style={{ gridTemplateColumns: "30px auto" }}
            >
              <div className="newGradingCheckbox">
                {reviewLater && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="newGradingCheckboxIcon"
                  />
                )}
              </div>
              <p className="newGradingCheckboxText">Review Later</p>
              {this.state.reviewLaterLoading && (
                <div className="highYieldLoadingContainer">
                  <MDSpinner size={30} singleColor={"#fff"} />
                </div>
              )}
            </div>

            {this.renderReadingOptions()}
          </div>
        </div>

        {/* Score cards */}
        <div
          className="newGradingCaseDetailsWrapper"
          data-highlight-container="true"
          data-highlight-key="caseDetails"
          data-highlight-type="caseDetails"
        >
          <div className="newGradingCaseDetailContainer">
            <div className="newGradingCaseDetailIconContainer">
              <FontAwesomeIcon
                icon={faUser}
                className="newGradingCaseDetailIcon"
              />
            </div>
            <h3 className="newGradingCaseDetailTitle">Your Score</h3>
            <h2 className="newGradingCaseDetailValue">{grade.GradeForCase}%</h2>
          </div>

          <div className="newGradingCaseDetailContainer">
            <div className="newGradingCaseDetailIconContainer">
              <FontAwesomeIcon
                icon={faUsers}
                className="newGradingCaseDetailIcon"
              />
            </div>
            <h3 className="newGradingCaseDetailTitle">
              Average First Attempt Score
            </h3>
            <h2 className="newGradingCaseDetailValue">
              {typeof grade.AverageFirstAttempt === "number"
                ? grade.AverageFirstAttempt.toFixed(1)
                : "\u2014"}
              %
            </h2>
          </div>

          <div className="newGradingCaseDetailContainer">
            <div className="newGradingCaseDetailIconContainer">
              <FontAwesomeIcon
                icon={faUserDoctorMessage}
                className="newGradingCaseDetailIcon"
              />
            </div>
            <h3 className="newGradingCaseDetailTitle">Case Diagnosis</h3>
            <h2 className="newGradingCaseDetailValue">
              {feedback.FinalDiagnosis}
            </h2>
          </div>
        </div>

        {/* Graded sections */}
        {GRADING_SECTIONS.map((section, sectionIndex) => {
          const hasAny = feedback.GradeOut.some(function (item) {
            return section.title.includes(item.GradeCategory);
          });
          if (!hasAny) return null;

          return (
            <div
              className="newGradingFeedbackItemContainer"
              data-highlight-container="true"
              data-highlight-key={section.title}
              data-highlight-type="feedbackItem"
              key={"section-" + sectionIndex}
            >
              <h2 className="newGradingFeedbackItemTitle">
                {section.title}:{" "}
                {feedback[section.gradeValue]
                  ? feedback[section.gradeValue]
                  : 0}
                %
              </h2>
              <h3 className="newGradingFeedbackItemValue">
                Weight given for total score: {feedback[section.weightValue]}%
              </h3>
              <div className="newGradingFeedbackItemContentWrapper">
                {feedback.GradeOut.map((item, index) => {
                  if (!section.title.includes(item.GradeCategory)) return null;

                  const optional =
                    item.EventName && item.EventName.includes("Optional Order");
                  const verdict = !showVerdicts
                    ? "neutral"
                    : item.Correct
                      ? "correct"
                      : optional
                        ? "optional"
                        : "incorrect";
                  const verdictClass = !showVerdicts
                    ? "newGradingFeedbackAnswerNeutral"
                    : item.Correct
                      ? "newGradingFeedbackAnswerCorrect"
                      : optional
                        ? "newGradingFeedbackAnswerNeutral"
                        : "newGradingFeedbackAnswerIncorrect";

                  return (
                    <div
                      key={"grade-" + sectionIndex + "-" + index}
                      className={
                        "newGradingFeedbackAnswerWrapper " + verdictClass
                      }
                      style={this.getReadingStyle(verdict)}
                    >
                      <div
                        className="newGradingFeedbackAnswerHeader"
                        style={{
                          marginBottom:
                            !item.OrderNames && !item.GradeExplanation
                              ? "0px"
                              : "15px",
                        }}
                      >
                        <div className="newGradingLeftBlock">
                          <h4 className="newGradingFeedbackAnswerTitle">
                            {(item.EventName.includes("Correctly Ordered") ||
                              item.EventName.includes("Should have Ordered")) &&
                            !showVerdicts
                              ? "Suggested Order"
                              : item.EventName}
                          </h4>
                          {item.EventName.includes("Suboptimal") && (
                            <div className="newGradingSuboptimalBlock">
                              <span className="newGradingSuboptimalFirst">
                                Suboptiomal
                              </span>
                              <span className="newGradingSuboptimalSecond">
                                Less Preferred
                              </span>
                            </div>
                          )}
                        </div>
                        {showVerdicts &&
                          this.renderImportanceIndicator(
                            item.Importance,
                            item.Correct
                              ? "correct"
                              : optional
                                ? "neutral"
                                : "incorrect",
                          )}
                      </div>
                      {item.OrderNames && item.OrderNamesList.length === 0 && (
                        <ul className="newGradingFeedbackAnswerItemList">
                          <li className="newGradingFeedbackAnswerItem">
                            {item.OrderNames}
                          </li>
                        </ul>
                      )}
                      {item.OrderNamesList.length !== 0 && (
                        <ul className="newGradingFeedbackAnswerItemList">
                          <li className="newGradingFeedbackAnswerItem">
                            {item.OrderNamesList.map(function (name, i) {
                              return (
                                name +
                                (i !== item.OrderNamesList.length - 1
                                  ? " / "
                                  : "")
                              );
                            })}
                          </li>
                        </ul>
                      )}
                      {item.GradeExplanation && (
                        <p className="newGradingFeedbackReason">
                          <strong>Reason: </strong>
                          {item.GradeExplanation}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Case summary */}
        {feedback.SUMMARY && (
          <div
            className="newGradingCaseSummaryContainer"
            style={this.getReadingStyle("neutral")}
            data-highlight-container="true"
            data-highlight-key="caseSummary"
            data-highlight-type="caseSummary"
          >
            <h2
              className="newGradingCaseSummaryTitle"
              style={{ color: "inherit" }}
            >
              Case Summary
            </h2>
            <p
              className="newGradingCaseSummaryText"
              style={{ color: "inherit" }}
            >
              {feedback.SUMMARY}
            </p>
          </div>
        )}

        {/* Average orders */}
        {this.getAverageOrders() && (
          <div className="newGradingCaseSummaryContainer">
            <h2 className="newGradingCaseSummaryTitle">Average Orders</h2>
            <div>
              <div className="averageOrdersContainer">
                <div className="averageOrdersLeftContainer">
                  <div>
                    {"Number of orders you ordered for this case: "}
                    <span className="gradeNumber">
                      {this.getAverageOrders().NumberOrders || 0}
                    </span>
                  </div>
                  <div>
                    {"All users' average orders for first case attempt >85%: "}
                    <span className="gradeNumber">
                      {
                        this.getAverageOrders().AverageOrders
                          .average_num_orders_85_above
                      }
                    </span>
                    {" orders"}
                  </div>
                  <div>
                    {"Your Z-score: "}
                    <span className="gradeNumber">
                      {this.getAverageOrders().Zscore.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="averageOrdersLeftContainer">
                  <div
                    className="averageOrdersChartContainer"
                    style={{
                      height: "300px",
                      width: "100%",
                      maxWidth: "800px",
                      marginTop: "20px",
                    }}
                  >
                    <Line
                      data={this.getAverageOrdersChartData()}
                      options={this.getAverageOrdersChartOptions()}
                    />
                  </div>
                  <span
                    style={{
                      color: this.getAverageOrders().Color,
                      textAlign: "center",
                    }}
                  >
                    {this.getAverageOrders().Interpretation}
                    {
                      ". The curve shows how common each order count is among users scoring >85% on first attempts; the area under the curve totals 100%"
                    }
                  </span>
                </div>
              </div>
              <div className="disclaimer">
                There's no evidence that the USMLE penalizes users for
                non-graded orders (except for medication, invasive orders, and
                certain radiological studies). However, excessive ordering to
                manipulate the system may incur penalties. Currently, your score
                isn't penalized for exceeding the average number of orders by
                more than one standard deviation, but we suggest keeping your
                order count within the typical range of the average user.
              </div>
            </div>
          </div>
        )}

        {/* Action log */}
        {feedback.ActionLogList && feedback.ActionLogList.length > 0 && (
          <ActionLog
            actionLog={feedback.ActionLogList}
            selectedActionLogItem={this.state.selectedActionLogItem}
            updateSelectedActionLogItem={(newSelection) => {
              this.setState({ selectedActionLogItem: newSelection });
            }}
            completionDate={feedback.CompletionDate}
          />
        )}
      </div>
    );
  }
}

export default MultiGradeView;
